/**
* @author: Rejo Varghese
* @description: This page is used to view the system info
*/

import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table } from 'antd';
import { getSystemInfo } from '../../actions/admin.action';

export const SystemInfoPage = () => {
    const [systemInfo, setSystemInfo] = useState(null);

    useEffect(() => {
        const fetchSystemInfo = async () => {
            getSystemInfo((data) => {
                setSystemInfo(data);
            }, (error) => {
                console.log(error);
            });
        }
        fetchSystemInfo();
    }, []);

    // Function to render dynamic table based on JSON structure
    const renderDynamicTable = (data) => {
        if (!data || typeof data !== 'object') {
            return <p>No data available</p>;
        }

        return Object.entries(data).map(([parentKey, value]) => {
            // If the value is an array, create a table
            if (Array.isArray(value)) {
                let valueLength = value.length;
                const isSummaryTable = valueLength > 1 && value[valueLength - 1].isSummaryRow === true? true : false;
                const columns = value.length > 0 ? Object.keys(value[0]).map(key => ({
                    title: key.charAt(0).toUpperCase() + key.slice(1), // Capitalize first letter
                    dataIndex: key,
                    key: key,
                    render: (text, record, rowIndex) => {
                        const displayText = typeof text === 'object' ? JSON.stringify(text) : text;
                        // Make last row bold if isSummaryTable is true
                        if (isSummaryTable && rowIndex === valueLength - 1) {
                            return <strong>{displayText}</strong>;
                        }
                        return displayText;
                    }
                })) : [];

                return (
                    <Card key={parentKey} style={{ marginBottom: 16 }}>
                        <h2 style={{ marginBottom: 16}}>
                            {parentKey.charAt(0).toUpperCase() + parentKey.slice(1)}
                        </h2>
                        <Table 
                            columns={columns} 
                            dataSource={value.map((item, index) => ({ ...item, key: index }))}
                            pagination={false}
                            size="small"
                        />
                    </Card>
                );
            }
            // If the value is an object but not an array, display as key-value pairs
            else if (typeof value === 'object' && value !== null) {
                const tableData = Object.entries(value).map(([key, val]) => ({
                    key: key,
                    property: key.charAt(0).toUpperCase() + key.slice(1),
                    value: typeof val === 'object' ? JSON.stringify(val) : String(val)
                }));

                const columns = [
                    {
                        title: 'Property',
                        dataIndex: 'property',
                        key: 'property',
                        width: '40%'
                    },
                    {
                        title: 'Value',
                        dataIndex: 'value',
                        key: 'value',
                        width: '60%'
                    }
                ];

                return (
                    <Card key={parentKey} style={{ marginBottom: 16 }}>
                        <h2 style={{ marginBottom: 16}}>
                            {parentKey.charAt(0).toUpperCase() + parentKey.slice(1)}
                        </h2>
                        <Table 
                            columns={columns} 
                            dataSource={tableData}
                            pagination={false}
                            size="small"
                        />
                    </Card>
                );
            }
            // If the value is a primitive, display as simple key-value
            else {
                return (
                    <Card key={parentKey} style={{ marginBottom: 16 }}>
                        <h2 style={{ marginBottom: 16}}>
                            {parentKey.charAt(0).toUpperCase() + parentKey.slice(1)}
                        </h2>
                        <p><strong>Value:</strong> {String(value)}</p>
                    </Card>
                );
            }
        });
    };

    return (
        <div style={{ padding: 24 }}>
            <Card>
                <Row>
                    <Col span={24}>
                        <h1 style={{ marginBottom: 24}}>System Info</h1>
                        {systemInfo ? renderDynamicTable(systemInfo) : <p>Loading system information...</p>}
                    </Col>
                </Row>
            </Card>
        </div>
    );
}